import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { ExerciseDraft, TrackingField } from "../lib/builder";
import { readStep } from "../../../supabase/functions/_shared/coachTabs";
import {
  DRAFT_SAYS, MAX_ROWS, MAX_STEPS, NOW_LABEL, PAUSE, RESUME, TARGET_FIELDS, type Part, type Raw,
  type RowState, befores, changes, isStanding, newStep, notYet, nowState, actionView, stepLabel, stepWords, storedRaws, tabsFit,
  targetView, withActionSets, withExercise, withPause, withTargetSets, withTwice, withWeeks, writeOf,
} from "../lib/ruleTabsEditor";
import { TWICE, effectiveRule, type RuleChoice } from "../lib/workoutEngine";
import { ExercisePick, TargetField, type LadderExercise, type Loaded } from "./WorkoutEngine";

/* 🔴 F15 STEP 4.1a — THE CARD IS THE EDITOR (his words 2026-10-31, twice: *"under each exercise title have tabs named maybe
   step1 step2 … inside each tab, a target and a trigger"*; *"why again target and action have their reps and kg … we have these
   anyway in each exercise … anything we change, will mean for that particular step and that particular target or action"*;
   then *"Yes, add Now"*). The model is `lib/ruleTabsEditor.ts`; this is the strip under the title and the LENS the card draws
   through: on `Now` the card is the row as written (the draft's own Save), on a step's Target or Action the card's set table,
   tempo, each side and note show that half and every edit goes to the step instead.

   ⚠️ WRITES ARE IMMEDIATE AND WHOLE, like the rule menu's (`useWorkoutEngine.setRule`): never part of the draft's Save — the
   list hangs on the ROOT workout and the library exercise (`coach_workout_rules`). A finished gesture writes the WHOLE list;
   a typed cell is finished when the typing pauses (`WRITE_AFTER_MS`), and a card that goes away writes what it holds. A step
   is written only once the server's reader accepts it (`writeOf`), a new step only once its Action changes something — until
   then the card says why under the pair. A write the server refuses shows its sentence and the strip goes back to the list
   as stored. */

const WRITE_AFTER_MS = 600;

export type RowStepsInput = {
  engine: Loaded; libraryConfig: unknown; library: readonly LadderExercise[];
  write: (choice: RuleChoice) => Promise<boolean>;
};

/** What the card draws through the strip. `onChange` null = the row's own (Now). */
export type RowLens = {
  bar: ReactNode;
  view: ExerciseDraft;
  onChange: ((e: ExerciseDraft) => void) | null;
  part: "now" | Part;
  /** The card SHOWS the half and cannot edit it: a change of exercise, a pause, a resume, a preset. */
  frozen: boolean;
  paused: boolean;
  title: ReactNode | null;
  notePlaceholder: string | null;
  /** A weeks target (after a Pause) stands where the set table is. */
  table: ReactNode | null;
};

export function useRowSteps(input: RowStepsInput | null, exercise: ExerciseDraft): RowLens | null {
  const [sel, setSel] = useState(-1);                     // -1 = Now
  const [part, setPart] = useState<Part>("target");
  const [local, setLocal] = useState<Raw[] | null>(null);
  const [draft, setDraft] = useState<Raw | null>(null);
  const timer = useRef<number | null>(null);
  const pending = useRef<(() => void) | null>(null);
  const library = input?.library;
  const byId = useMemo(() => new Map((library ?? []).map((e) => [e.id.toLowerCase(), e])), [library]);
  const names = useMemo(() => new Map([...byId].map(([k, e]) => [k, e.name])), [byId]);

  const engine = input?.engine ?? null;
  const exId = exercise.libraryExerciseId;
  const eff = engine === null ? null : effectiveRule(engine, exId, input?.libraryConfig ?? null);
  const configKey = JSON.stringify(eff?.config ?? null);
  /* The stored list moved (this card's own write landing, or a preset picked): the card reads it again — unless a write is
     still waiting, which carries what the coach typed since. */
  useEffect(() => { if (timer.current === null) setLocal(null); }, [configKey]);
  /* A card that goes away (the builder closed, the row removed) writes what it holds. */
  useEffect(() => () => {
    if (timer.current !== null) { window.clearTimeout(timer.current); timer.current = null; pending.current?.(); }
  }, []);

  if (input === null || engine === null || eff === null || !engine.progress) return null;
  /* A row a ladder or a step of ANOTHER row moved here reads the rest of that list: it is edited on the row that owns it. A row
     tracking rounds or distance is frozen whole today (§9) — both keep the rule line. */
  if ((eff.source === "workout" && !engine.rules.has(exId)) || !tabsFit(exercise.trackingFields)) return null;
  const { write } = input;

  const stored = storedRaws(eff.config);
  const held = local ?? stored.raws;
  /* His 11-01 word: no presets menu — a row's steps are his. A preset (a standing tab: the library's rule, or a migrated §5
     kind) stays its row's Step 1 until a step of his is added: the new step then takes its place, and is written in its place
     once it reads and moves the row (a draft left unwritten leaves the preset as stored). */
  const raws = draft !== null && held.length > 0 && isStanding(held[held.length - 1]) ? held.slice(0, -1) : held;
  const list = draft === null ? raws : [...raws, draft];
  const at = sel < list.length ? sel : -1;
  const bs = befores(list, nowState(exercise));

  function schedule(next: Raw[]) {
    const config = writeOf(next);
    if (config === null) return;
    if (timer.current !== null) window.clearTimeout(timer.current);
    const go = () => { void write({ key: "steps", config }).then((ok) => { if (!ok) setLocal(null); }); };
    pending.current = go;
    timer.current = window.setTimeout(() => { timer.current = null; pending.current = null; go(); }, WRITE_AFTER_MS);
  }
  function commit(next: Raw[]) { setLocal(next); schedule(next); }
  /** The step at `i` changed: a stored one is written with the list; the draft joins the list once it reads and moves the row. */
  function edit(i: number, raw: Raw) {
    if (draft !== null && i === raws.length) {
      const step = readStep(raw);
      if (step !== null && changes(step, bs[i]!)) { setDraft(null); commit([...raws, raw]); } else setDraft(raw);
      return;
    }
    commit(raws.map((r, j) => (j === i ? raw : r)));
  }
  /** A new step opens on the row as the whole list leaves it (`{}` reads as nothing, so it changes nothing); after a preset,
   *  on the row as written, in the preset's place. */
  function add() {
    const base = list.length > 0 && isStanding(list[list.length - 1]) ? list.slice(0, -1) : list;
    const before = befores([...base, {}], nowState(exercise))[base.length]!;
    setDraft(newStep(before, trackingOf(before)));
    setSel(base.length);
    setPart("target");
  }
  function removeLast() {
    setSel(list.length - 2);
    if (draft !== null) { setDraft(null); return; }
    commit(raws.slice(0, -1));
  }
  function trackingOf(state: RowState): TrackingField[] {
    const lib = byId.get(state.exerciseId);
    return state.exerciseId === exId.toLowerCase() || lib === undefined ? exercise.trackingFields
      : lib.trackingFields as TrackingField[];
  }

  const last = list.length - 1;
  const canAdd = (list.length < MAX_STEPS || isStanding(list[last])) && draft === null && stored.brokenAt === null;
  const raw = at >= 0 ? list[at]! : null;
  const before = at >= 0 ? bs[at]! : null;
  const standing = raw !== null && isStanding(raw);
  const caption = raw === null ? null : notYet(raw) ?? (draft !== null && at === raws.length ? DRAFT_SAYS : null);

  /* ── the lens: what the card shows for the chosen half, and where its edits go ── */
  const base: ExerciseDraft = before === null ? exercise : {
    ...exercise,
    libraryExerciseId: before.exerciseId, name: byId.get(before.exerciseId)?.name ?? exercise.name,
    trackingFields: trackingOf(before), sets: before.sets, tempo: before.tempo, eachSide: before.eachSide, note: before.note,
  };
  let view = base;
  let onChange: RowLens["onChange"] = null;
  let frozen = false;
  let paused = false;
  let title: ReactNode = null;
  let table: ReactNode = null;
  let notePlaceholder: string | null = null;
  let extra: ReactNode = null;
  if (raw !== null && before !== null && standing) frozen = true;
  else if (raw !== null && before !== null && part === "target") {
    const tv = targetView(raw, before);
    const tracking = base.trackingFields.filter((t) => TARGET_FIELDS.includes(t));
    notePlaceholder = "Note on this target";
    if (tv.kind === "sets") {
      view = { ...base, trackingFields: tracking, sets: tv.sets, note: tv.note };
      onChange = (v) => { if (v.sets.length <= MAX_ROWS) edit(at, withTargetSets(raw, v.sets, tracking, v.note)); };
      extra = (
        <button type="button" className="ours-chip wb-ladder-twice" aria-pressed={tv.twice}
                onClick={() => edit(at, withTwice(raw, !tv.twice))}>{TWICE}</button>
      );
    } else if (tv.kind === "weeks") {
      view = { ...base, note: tv.note };
      onChange = (v) => edit(at, withWeeks(raw, tv.weeks, v.note));
      table = (
        <p className="wb-step-weeks">
          <label className="wb-ladder-num">
            <span>After</span>
            <TargetField value={tv.weeks} unit="reps" label="Weeks paused" onCommit={(n) => edit(at, withWeeks(raw, n, tv.note))} />
            <span>{tv.weeks === 1 ? "week" : "weeks"}</span>
          </label>
        </p>
      );
    }
  } else if (raw !== null && before !== null) {
    const av = actionView(raw, before);
    if (av.kind === "set") {
      view = { ...base, sets: av.sets, tempo: av.tempo, eachSide: av.eachSide, note: av.note };
      onChange = (v) => { if (v.sets.length <= MAX_ROWS) edit(at, withActionSets(raw, v, before)); };
    } else {
      frozen = true;
      paused = av.kind === "deactivate";
    }
    if (before.hidden) {
      extra = <button type="button" className="ours-chip" aria-pressed={true} disabled>{RESUME}</button>;
    } else {
      extra = (
        <button type="button" className="ours-chip" aria-pressed={av.kind === "deactivate"}
                onClick={() => edit(at, withPause(raw, av.kind !== "deactivate", before))}>{PAUSE}</button>
      );
      /* In Action the title IS the exercise picker: another exercise = change exercise; its own name back = no change. */
      const value = av.kind === "next" ? av.next : before.exerciseId;
      const picks = [...byId.values()].filter((e) => (e.hiddenAt ?? null) === null || e.id.toLowerCase() === before.exerciseId)
        .sort((a, b) => a.name.localeCompare(b.name));
      title = (
        <span className="wb-step-swap">
          {av.kind === "next" && <span className="wb-ladder-arrow" aria-hidden="true">→</span>}
          <ExercisePick value={value} picks={picks} label="Change exercise" onPick={(id) => edit(at, withExercise(raw, id, before))} />
        </span>
      );
    }
  }

  const bar = (
    <div className="wb-steps">
      <div className="wb-steps-bar">
        <div className="wb-steps-strip" role="tablist" aria-label={`${exercise.name}: steps`}>
          <span className="wb-step-wrap">
            <button type="button" role="tab" className="wb-step" aria-selected={at === -1} onClick={() => setSel(-1)}>{NOW_LABEL}</button>
          </span>
          {list.map((r, i) => (
            <span key={i} className={i === last ? "wb-step-wrap is-last" : "wb-step-wrap"}>
              <button type="button" role="tab" className={draft !== null && i === raws.length ? "wb-step is-draft" : "wb-step"}
                      aria-selected={i === at} title={stepWords(r, names)}
                      onClick={() => setSel(i)}>{stepLabel(i)}</button>
              {i === last && (
                <button type="button" className="wb-step-remove" aria-label={`Remove ${stepLabel(i)}`} onClick={removeLast}>×</button>
              )}
            </span>
          ))}
          {canAdd && <button type="button" className="wb-step-add" aria-label="Add a step" onClick={add}>+</button>}
        </div>
      </div>
      {raw !== null && !standing && (
        <span className="wb-step-modes">
          <span className="wb-step-parts" role="tablist" aria-label={`${stepLabel(at)}: target or action`}>
            {(["target", "action"] as const).map((p) => (
              <button key={p} type="button" role="tab" className="wb-step-part" aria-selected={part === p}
                      onClick={() => setPart(p)}>{p === "target" ? "Target" : "Action"}</button>
            ))}
          </span>
          {extra}
          {caption !== null && <span className="ht-caption wb-step-not-yet">{caption}</span>}
        </span>
      )}
      {stored.brokenAt !== null && <span className="wb-rule-lacks">{stepLabel(stored.brokenAt)} can't be read — not shown</span>}
    </div>
  );
  return { bar, view, onChange, part: raw === null ? "now" : standing ? "action" : part, frozen, paused, title, notePlaceholder, table };
}
